/**
 * Les numeros de telephone d'un prospect importe.
 *
 * Extrait d'AddProspectDialog le 2026-09-30, avec le reste de la logique
 * d'import : un numero se normalise et se reformate sans qu'aucune fenetre
 * soit ouverte.
 */

/* Same normalization as the server's check-blacklist endpoint — strip every
   character except digits and a leading +. The two halves MUST stay aligned. */
export function normalizePhone(p: unknown): string {
    return String(p ?? '').replace(/[^\d+]/g, '');
}

/* #46 -- normalisation vers E.164 (France).
   ATTENTION : distincte de `normalizePhone` juste au-dessus, qui sert a
   COMPARER (blacklist, alignement avec le serveur). Celle-ci sert a ECRIRE.
   Rend null quand le numero n'est pas interpretable : on ne devine pas. */
export function reformaterTelephone(brut: unknown): string | null {
    const t = String(brut ?? '').trim();
    if (!t) return null;
    const plus = t.startsWith('+');
    let chiffres = t.replace(/\D/g, '');
    if (!chiffres) return null;

    // 2026-09-15 — prefixe international « 00 » : 0033612998877 est la meme
    // chose que +33612998877. Sans ce cas la fonction rendait null, donc le
    // numero n'etait pas repare du tout.
    if (!plus && chiffres.startsWith('00')) chiffres = chiffres.slice(2);

    // 2026-09-15 — ZERO DE TRANSIT FRANCAIS. Dans « +33(0)6 55 44 33 22 » le
    // (0) est un zero A RETIRER : il ne se compose pas depuis l'international.
    // Le decapage des separateurs en faisait un chiffre, la chaine passait a 12
    // caracteres, ratait le cas `length === 11` et tombait dans le fourre-tout
    // `if (plus) return '+' + chiffres` — produisant « +330655443322 », soit un
    // numero de 13 chiffres INJOIGNABLE. La fonction censee reparer les numeros
    // non appelables en fabriquait un, juste sous un bandeau qui annonce
    // « un numero mal formate n'est pas appelable ».
    // Cf. BUGS-OUVERTS.md (TEL-1).
    if (chiffres.startsWith('330') && chiffres.length === 12) {
      chiffres = '33' + chiffres.slice(3);
    }

    // deja en +33 ... : on se contente de retirer les separateurs
    if (chiffres.startsWith('33') && chiffres.length === 11) return '+' + chiffres;
    if (plus) return '+' + chiffres;
    // 0X XX XX XX XX -> +33X XX XX XX XX
    if (chiffres.length === 10 && chiffres.startsWith('0')) return '+33' + chiffres.slice(1);
    // 33XXXXXXXXX sans le +
    if (chiffres.length === 11 && chiffres.startsWith('33')) return '+' + chiffres;
    // 9 chiffres sans le 0 initial
    if (chiffres.length === 9 && /^[1-9]/.test(chiffres)) return '+33' + chiffres;
    return null;
}

/* Le numero a-t-il besoin d'etre reecrit ? */
export function telephoneAReformater(brut: unknown): boolean {
    const cible = reformaterTelephone(brut);
    return !!cible && cible !== String(brut ?? '').trim();
}

/**
 * chantier 2 -- `prospect_mobile` n'est pas un champ du schema ProspectQueue :
 * comme dans le formulaire manuel (#96), une colonne mappee dessus alimente
 * `additional_phones` avec le libelle "Portable", jamais `prospect_phone`
 * (le numero principal). Mutates `prospect` in place and returns it, for
 * chaining in the import loop. Exported as a pure function so the mapping
 * can be tested without rendering the whole import wizard.
 */
/** Un prospect en cours d'import, dont les champs varient avec le fichier. */
export interface ProspectImporte {
  prospect_mobile?: string | null;
  additional_phones?: Array<{ number: string; label: string }>;
  [autreChamp: string]: unknown;
}

export function applyMobileToAdditionalPhones(
  prospect: ProspectImporte,
  reformaterNumeros: boolean,
): ProspectImporte {
    if (!Object.prototype.hasOwnProperty.call(prospect, 'prospect_mobile')) return prospect;
    const mobileBrut = String(prospect.prospect_mobile || '').trim();
    delete prospect.prospect_mobile;
    if (mobileBrut) {
        const mobileCorrige = reformaterNumeros && telephoneAReformater(mobileBrut)
            ? (reformaterTelephone(mobileBrut) || mobileBrut)
            : mobileBrut;
        // i18n-exempt: phone label stored on the prospect record
        prospect.additional_phones = [{ number: mobileCorrige, label: 'Portable' }];
    }
    return prospect;
}
